import React, { useState, useCallback, useMemo, useEffect } from "react";
import classNames from "classnames";
import { TextInput } from "frontend/ui-components/design-system/text-input";
import { MagnifyingGlassIcon } from "frontend/ui-components/picker/magnifying-glass-icon";
import {
  MondayImportItemsContextProvider,
  useMondayImportItemsContext,
} from "frontend/canvas-designer-new/monday-import-items/monday-import-items-context";
import { MondayItemsList } from "frontend/canvas-designer-new/monday-import-items/monday-items-list/monday-items-list";
import { MondayBreadcrumbs } from "frontend/canvas-designer-new/monday-import-items/monday-breadcrumbs/monday-breadcrumbs";
import { GanttChartGroup } from "./synced-gantt-chart.types";
import styles from "./synced-gantt-chart.module.css";
import SyncedWidgetHeader from "../../components/synced-widget-header";
import { noop } from "utils/fn-utils";
import tracking from "frontend/tracking";

interface GanttItemsSelectionProps {
  groups: GanttChartGroup[];
  isLoading: boolean;
  isCreating?: boolean;
  hasMoreItems: boolean;
  loadMoreItems: () => void;
  onImport: (selectedItemIds: string[]) => void;
}

function GanttItemsSelectionContent({
  groups,
  isLoading,
  isCreating = false,
  hasMoreItems,
  loadMoreItems,
  onImport,
}: GanttItemsSelectionProps) {
  const [search, setSearch] = useState("");
  const { currentItem, breadcrumbs, clearBreadcrumbs } = useMondayImportItemsContext();
  const [currentItemColor, setCurrentItemColor] = useState<string | undefined>();
  const [selectedItems, setSelectedItems] = useState<string[]>(() => {
    const allItemIds: string[] = [];
    for (const group of groups) {
      for (const item of group.items) {
        allItemIds.push(item.id);
        if (item.subItems) {
          for (const subItem of item.subItems) {
            if (subItem?.id) {
              allItemIds.push(subItem.id);
            }
          }
        }
      }
    }
    return allItemIds;
  });

  useEffect(() => {
    if (!currentItem) {
      setCurrentItemColor(undefined);
    }
  }, [currentItem]);

  useEffect(() => {
    if (currentItem) {
      for (const group of groups) {
        const item = group.items.find((item) => item.id === currentItem.id);
        if (item) {
          setCurrentItemColor(group.color);
          break;
        }
      }
    }
  }, [currentItem, groups]);

  const handleImport = useCallback(() => {
    onImport(selectedItems);
    clearBreadcrumbs();
    setCurrentItemColor(undefined);
  }, [selectedItems, onImport, clearBreadcrumbs]);

  const formattedGroups = useMemo(() => {
    return groups.map((group) => ({
      id: group.id,
      name: group.name,
      color: group.color,
      items: group.items.map((item) => ({
        id: item.id,
        name: item.name,
        subItems: item.subItems?.map((subItem) => ({
          id: subItem.id,
          name: subItem.name,
        })),
      })),
    }));
  }, [groups]);

  const isButtonDisabled = selectedItems.length === 0 || isCreating;

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <SyncedWidgetHeader title="Import items" subtitle="Select items to place on the Gantt" />

          <div className={styles.searchContainer}>
            <TextInput
              leftIcon={<MagnifyingGlassIcon />}
              variant="small"
              placeholder="Search board's items"
              onChange={(e) => setSearch(e.target.value)}
              clearable={true}
            />
          </div>
        </div>

        {breadcrumbs.length > 0 && <MondayBreadcrumbs />}

        <div className={styles.divider} />

        <MondayItemsList
          groups={formattedGroups}
          searchValue={search}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          currentItem={currentItem || undefined}
          currentItemColor={currentItemColor}
          loadMoreItems={loadMoreItems}
          hasMoreItems={hasMoreItems}
          itemsLoading={isLoading}
          onDragEnd={noop}
          onSubItemsClick={noop}
          showSelectAll={true}
        />

        <div className={styles.overlay} />

        <div className={styles.importButtonContainer}>
          <div className={styles.stepIndicatorItems}>
            <div className={styles.stepDot} />
            <div className={classNames(styles.stepDot, styles.stepDotActive)} />
          </div>

          <button
            disabled={isButtonDisabled}
            className={classNames(styles.importButton, {
              [styles.importButtonDisabled]: isButtonDisabled,
              [styles.importButtonLoading]: isCreating,
            })}
            onClick={() => {
              handleImport();
              tracking.trackAnalyticsEvent("cim_gantt_chart_create_clicked");
            }}
          >
            {isCreating
              ? "Creating..."
              : `Import ${selectedItems.length} ${selectedItems.length === 1 ? "item" : "items"}`}
          </button>
        </div>
      </div>
    </>
  );
}

export function GanttItemsSelection(props: GanttItemsSelectionProps) {
  return (
    <MondayImportItemsContextProvider>
      <GanttItemsSelectionContent {...props} />
    </MondayImportItemsContextProvider>
  );
}
